import {
  DndContext,
  PointerSensor,
  pointerWithin,
  restrictToHorizontalAxis,
  useSensor,
  useSensors,
} from '@github-ui/drag-and-drop'
import {forwardRef, memo, useCallback, useMemo} from 'react'

import {
  type FieldGroupUIType,
  GroupByStatKey,
  SliceByApplied,
  SliceByRemoved,
  TableHeaderMenuUI,
} from '../../api/stats/contracts'
import {useHorizontalGroupedBy} from '../../features/grouping/hooks/use-horizontal-grouped-by'
import {useSliceBy} from '../../features/slicing/hooks/use-slice-by'
import {usePostStats} from '../../hooks/common/use-post-stats'
import {columnIsGrouped, getColumnModel} from '../../hooks/tanstack-table/helpers/column-api'
import {getHeaderColumnModel} from '../../hooks/tanstack-table/helpers/header-api'
import type {ColumnInstance, HeaderGroup} from '../../hooks/tanstack-table/helpers/types'
import {useColumnReorderingDragHandlers} from '../../hooks/tanstack-table/providers/columns/column-order-dnd-provider'
import {useTableColumnResizing} from '../../hooks/tanstack-table/providers/table-provider'
import {useRootElement} from '../../hooks/use-root-element'
import {useViewType} from '../../hooks/use-view-type'
import {useViews} from '../../hooks/use-views'
import type {ColumnModel} from '../../models/column-model'
import {DummyHeaderCell} from './header/dummy-table-header-cell'
import {TableHeaderCell} from './header/table-header-cell'
import {useRecordComponent} from './performance-measurements'
import type {TableDataType} from './table-data-type'
import styles from './table-header.module.css'

type Props = {
  headerGroups: Array<HeaderGroup<TableDataType>>
  height: number
  scrollRef: React.RefObject<HTMLElement | null>
  tableWidth: number
}

const autoScroll = {threshold: {x: 0.3, y: 0}}
const modifiers = [restrictToHorizontalAxis]

/**
 * The row containing the table header cells.
 */
export const TableHeader = memo(
  forwardRef<HTMLDivElement, Props>(function TableHeader(props, ref) {
    // NOTE: Escape hatch, since we don't otherwise depend on column resizing
    // state.
    useTableColumnResizing()
    const {currentView} = useViews()
    const {viewType} = useViewType()
    const {postStats} = usePostStats()
    useRecordComponent('Table', 'TableHeader', '')

    const {clearGroupedBy, setGroupedBy} = useHorizontalGroupedBy()
    const {setSliceField, clearSliceField, sliceField} = useSliceBy()

    const groupByColumn = useCallback(
      (column: ColumnInstance<TableDataType>, ui: FieldGroupUIType) => {
        const columnModel = getColumnModel(column)
        if (!columnModel) return
        if (!currentView) return
        if (columnIsGrouped(column)) {
          clearGroupedBy(currentView.number)
        } else {
          setGroupedBy(currentView.number, columnModel)
        }

        postStats({
          groupByEnabled: !columnIsGrouped(column),
          memexProjectColumnId: columnModel.id,
          key: GroupByStatKey,
          ui,
        })
      },
      [currentView, postStats, clearGroupedBy, setGroupedBy],
    )

    const sliceByColumn = useCallback(
      (column: ColumnModel) => {
        if (!currentView) return

        const isSliceField = sliceField === column

        if (isSliceField) {
          clearSliceField(currentView.number)
        } else {
          setSliceField(currentView.number, column)
        }

        postStats({
          name: isSliceField ? SliceByRemoved : SliceByApplied,
          ui: TableHeaderMenuUI,
          memexProjectColumnId: column.id,
          context: JSON.stringify({layout: viewType}),
        })

        return
      },
      [clearSliceField, currentView, postStats, setSliceField, sliceField, viewType],
    )

    const sensors = useSensors(
      useSensor(PointerSensor, {
        activationConstraint: {
          distance: 2, // constraint ensure that header menu click does not get registered as drag
        },
      }),
    )
    const dndEvents = useColumnReorderingDragHandlers()
    const rootElement = useRootElement()
    return (
      <DndContext
        modifiers={modifiers}
        sensors={sensors}
        collisionDetection={pointerWithin}
        autoScroll={autoScroll}
        accessibility={useMemo(() => ({container: rootElement}), [rootElement])}
        {...dndEvents}
      >
        <div
          ref={ref}
          style={{
            height: props.height,
            minWidth: props.tableWidth,
          }}
          className={styles.Box}
        >
          {props.headerGroups.map(headerGroup => {
            return (
              <div
                key={`headerGroup_${headerGroup.id}`}
                role="row"
                style={{display: 'flex', width: '100%'}}
                className={styles.Box_1}
              >
                {headerGroup.headers.map(header =>
                  getHeaderColumnModel(header) ? (
                    <TableHeaderCell
                      key={header.id}
                      header={header}
                      groupByColumn={groupByColumn}
                      sliceByColumn={sliceByColumn}
                      height={props.height}
                    />
                  ) : (
                    <DummyHeaderCell header={header} height={props.height} key={header.id} />
                  ),
                )}
              </div>
            )
          })}
        </div>
      </DndContext>
    )
  }),
)

TableHeader.displayName = 'TableHeader'
